/**
 * Fil d'actualité du portail étudiant.
 * - Liste chronologique (épinglées en haut)
 * - Filtres par type
 * - Détail au clic + réactions
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Pin, Calendar, MapPin, ExternalLink, ThumbsUp, Heart, Award,
  Flame, X, Loader2, Cake, ChevronRight, MessageCircle, ChevronUp,
  GraduationCap, Briefcase,
} from 'lucide-react';
import { PortailLayout } from '../PortailLayout';
import { portailApi, ActualiteItem } from '../services/portailApi';
import { ContenuExtensible } from '../../components/ContenuExtensible';
import { ComposerActualite } from '../components/ComposerActualite';
import { CommentairesActualite } from '../components/CommentairesActualite';

const TYPE_LABELS: Record<string, string> = {
  info: 'Information',
  seminaire: 'Séminaire',
  cours: 'Cours / Programme',
  soutenance: 'Soutenance',
  excursion: 'Excursion / Sortie',
  bal: 'Évènement festif',
  calendrier: 'Calendrier académique',
  livre: 'Publication / Livre',
  projet: 'Présentation projet',
  autre: 'Autre',
};

// Fix 2026-08-31 : palette unifiée façon Facebook — un seul style neutre
// pour tous les types. Le type reste indiqué par le texte du badge, mais
// plus de rainbow qui distrait l'œil et empêche de scanner le fil.
const TYPE_BADGE_CLS: Record<string, string> = {
  info: 'bg-gray-800 text-gray-300 border-gray-700',
  seminaire: 'bg-gray-800 text-gray-300 border-gray-700',
  cours: 'bg-gray-800 text-gray-300 border-gray-700',
  soutenance: 'bg-gray-800 text-gray-300 border-gray-700',
  excursion: 'bg-gray-800 text-gray-300 border-gray-700',
  bal: 'bg-gray-800 text-gray-300 border-gray-700',
  calendrier: 'bg-gray-800 text-gray-300 border-gray-700',
  livre: 'bg-gray-800 text-gray-300 border-gray-700',
  projet: 'bg-gray-800 text-gray-300 border-gray-700',
  autre: 'bg-gray-800 text-gray-300 border-gray-700',
};

const REACTIONS: { key: string; icon: React.ReactNode; label: string }[] = [
  { key: 'like', icon: <ThumbsUp size={16} />, label: 'J\'aime' },
  { key: 'coeur', icon: <Heart size={16} />, label: 'Coeur' },
  { key: 'bravo', icon: <Award size={16} />, label: 'Bravo' },
  { key: 'feu', icon: <Flame size={16} />, label: 'Top' },
];

function fmtDate(s: string | null): string {
  if (!s) return '';
  return new Date(s).toLocaleDateString('fr-FR', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

function fmtDateHeure(s: string | null): string {
  if (!s) return '';
  return new Date(s).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function fmtRelative(s: string): string {
  const date = new Date(s);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return 'à l\'instant';
  const min = Math.floor(sec / 60);
  if (min < 60) return `il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `il y a ${h} h`;
  const j = Math.floor(h / 24);
  if (j < 7) return `il y a ${j} j`;
  return fmtDate(s);
}

export default function Actualites() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState<ActualiteItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filtreType, setFiltreType] = useState<string>('all');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [annivs, setAnnivs] = useState<any[]>([]);
  const [moi, setMoi] = useState<{ prenoms: string; nom: string } | null>(null);

  const recharger = async () => {
    try {
      const r = await portailApi.actualitesList();
      setItems(r.results);
    } catch { /* silencieux */ }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await portailApi.actualitesList();
        if (mounted) setItems(r.results);
      } catch (e: any) {
        if (e?.status === 401) {
          portailApi.clearToken();
          navigate('/portail/login', { replace: true });
        } else if (mounted) setError(e?.message || 'Erreur');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    // Charger le profil pour l'avatar du composer
    portailApi.me().then((r: any) => {
      if (mounted) setMoi({ prenoms: r?.prenoms || r?.prenom || '', nom: r?.nom || '' });
    }).catch(() => {});

    // Charger les anniversaires en parallèle (silencieux si échec)
    portailApi.anniversairesDuJour()
      .then((r) => { if (mounted) setAnnivs(r.results || []); })
      .catch(() => { /* ignore */ });
    return () => { mounted = false; };
  }, [navigate]);

  // Auto-ouverture d'une actualité via ?id=N
  useEffect(() => {
    const id = params.get('id');
    if (id && items.length > 0) {
      const num = parseInt(id, 10);
      if (!isNaN(num) && items.find((i) => i.id === num)) setSelectedId(num);
    }
  }, [params, items]);

  const types = useMemo(() => {
    const set = new Set<string>();
    items.forEach((i) => set.add(i.type));
    return ['all', ...Array.from(set)];
  }, [items]);

  const filtered = useMemo(() => {
    if (filtreType === 'all') return items;
    return items.filter((i) => i.type === filtreType);
  }, [items, filtreType]);

  // Fix 2026-09-07 : le modal détail pousse une entrée history dédiée pour
  // que le bouton "retour" du navigateur ou du système ferme le dialogue
  // au lieu de quitter les Actualités.
  const isClosingViaPopRef = useRef(false);
  const handleSelect = (id: number) => {
    setSelectedId(id);
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, a_lu: true } : i)));
    // Marquer côté backend (sinon le badge non-lu de l'accueil reste figé)
    portailApi.actualiteDetail(id).catch(() => { /* silencieux */ });
    // Ajoute une entrée d'historique — pop = fermeture
    try {
      window.history.pushState({ actuModal: id }, '');
    } catch { /* ignore */ }
  };

  const handleClose = useCallback(() => {
    setSelectedId(null);
    if (params.get('id')) {
      params.delete('id');
      setParams(params, { replace: true });
    }
    // Si on ferme via UI, retire l'entrée history poussée à l'ouverture
    if (!isClosingViaPopRef.current) {
      try {
        if (window.history.state && (window.history.state as any).actuModal) {
          window.history.back();
        }
      } catch { /* ignore */ }
    }
    isClosingViaPopRef.current = false;
  }, [params, setParams]);

  // Intercepte le bouton "retour" du navigateur/système pour fermer le
  // dialogue au lieu de quitter la page Actualités.
  useEffect(() => {
    if (selectedId === null) return;
    const onPop = () => {
      isClosingViaPopRef.current = true;
      setSelectedId(null);
      if (params.get('id')) {
        params.delete('id');
        setParams(params, { replace: true });
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [selectedId, params, setParams]);

  const handleReact = async (id: number, type: string) => {
    const item = items.find((i) => i.id === id);
    if (!item) return;
    const newType = item.ma_reaction === type ? null : type;
    try {
      const r = await portailApi.actualiteReaction(id, newType);
      setItems((prev) =>
        prev.map((i) => {
          if (i.id !== id) return i;
          const reactions = { ...i.reactions };
          if (i.ma_reaction && reactions[i.ma_reaction]) {
            reactions[i.ma_reaction] = Math.max(0, reactions[i.ma_reaction] - 1);
          }
          if (r.ma_reaction) {
            reactions[r.ma_reaction] = (reactions[r.ma_reaction] || 0) + 1;
          }
          const nb_reactions_total = Object.values(reactions).reduce((a, b) => a + b, 0);
          return { ...i, ma_reaction: r.ma_reaction, reactions, nb_reactions_total };
        })
      );
    } catch (e) {
      // ignore
    }
  };

  const selected = selectedId ? items.find((i) => i.id === selectedId) || null : null;

  return (
    <PortailLayout showHeader title="Actualités" fullWidth>
      <div className="space-y-3">
        <button
          onClick={() => navigate('/portail/accueil')}
          className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-gray-200"
        >
          <ArrowLeft size={16} /> Retour
        </button>

        {/* Filtres types */}
        {types.length > 1 && (
          <div className="flex flex-wrap gap-1.5">
            {types.map((t) => (
              <button
                key={t}
                onClick={() => setFiltreType(t)}
                className={`px-2.5 py-1 text-xs font-medium rounded-full border transition-colors ${
                  filtreType === t
                    ? 'bg-blue-600 border-blue-500 text-white'
                    : 'bg-gray-900 border-gray-700 text-gray-300 hover:border-gray-600'
                }`}
              >
                {t === 'all' ? 'Tout' : TYPE_LABELS[t] || t}
              </button>
            ))}
          </div>
        )}

        {loading && (
          <div className="text-center py-12">
            <Loader2 className="inline-block animate-spin text-blue-400" size={28} />
            <p className="text-gray-500 text-sm mt-3">Chargement...</p>
          </div>
        )}

        {error && (
          <div className="bg-red-900/30 border border-red-800 rounded-lg p-3 text-sm text-red-300">
            {error}
          </div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div className="text-center py-12 text-gray-500 text-sm">
            Aucune actualité pour le moment.
          </div>
        )}

        {/* Carte spéciale anniversaires (en tête, toujours visible si > 0) */}
        {annivs.length > 0 && (
          <button
            onClick={() => navigate('/portail/anniversaires')}
            className="w-full text-left bg-gray-900 border border-amber-700/50 hover:border-amber-500 rounded-xl overflow-hidden transition-colors"
          >
            <div className="p-3.5 flex items-center gap-3">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-amber-500/20 border border-amber-500/60 flex items-center justify-center">
                <Cake size={22} className="text-amber-300" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold rounded border bg-amber-500/15 text-amber-300 border-amber-800">
                    Anniversaires
                  </span>
                  <span className="text-[10px] text-amber-300/70">aujourd'hui</span>
                </div>
                <div className="font-semibold text-gray-100 text-sm mt-1">
                  {annivs.length === 1
                    ? `${annivs[0].prenoms} ${annivs[0].nom} fête son anniversaire 🎂`
                    : `${annivs.length} étudiants fêtent leur anniversaire 🎂`}
                </div>
                <div className="text-[11px] text-amber-300/80 mt-0.5">
                  Touchez pour leur souhaiter joyeux anniversaire
                </div>
              </div>
              <div className="flex -space-x-2 flex-shrink-0">
                {annivs.slice(0, 3).map((a: any) =>
                  a.photo_url ? (
                    <img
                      key={a.matricule}
                      src={a.photo_url}
                      alt=""
                      className="w-8 h-8 rounded-full object-cover border-2 border-amber-700"
                    />
                  ) : (
                    <div
                      key={a.matricule}
                      className="w-8 h-8 rounded-full bg-amber-600 text-white flex items-center justify-center text-[10px] font-bold border-2 border-amber-700"
                    >
                      {(a.prenoms?.[0] || '') + (a.nom?.[0] || '')}
                    </div>
                  )
                )}
                {annivs.length > 3 && (
                  <div className="w-8 h-8 rounded-full bg-gray-700 text-white flex items-center justify-center text-[10px] font-bold border-2 border-amber-700">
                    +{annivs.length - 3}
                  </div>
                )}
              </div>
              <ChevronRight size={16} className="text-pink-300 flex-shrink-0" />
            </div>
          </button>
        )}

        {/* Composer : publication libre étudiante */}
        {!loading && (
          <ComposerActualite
            auteurNomAffiche={`${moi?.prenoms || ''} ${moi?.nom || ''}`.trim() || 'Vous'}
            onPublie={recharger}
          />
        )}

        {!loading && filtered.length > 0 && (
          <div className="space-y-2.5">
            {filtered.map((it) => (
              <ActualiteCard
                key={it.id}
                item={it}
                onClick={() => handleSelect(it.id)}
                onReact={(type) => handleReact(it.id, type)}
                onOpenComments={() => handleSelect(it.id)}
              />
            ))}
          </div>
        )}
      </div>

      {selected && (
        <DetailModal
          item={selected}
          onClose={handleClose}
          onReact={(type) => handleReact(selected.id, type)}
        />
      )}
    </PortailLayout>
  );
}

// ─── Carte liste ─────────────────────────────────────────────────

const ActualiteCard: React.FC<{
  item: ActualiteItem;
  onClick: () => void;
  onReact: (type: string) => void;
  onOpenComments: () => void;
}> = ({ item, onClick, onReact, onOpenComments }) => {
  // Fix 2026-08-31 : carte façon Facebook — fond uniforme, bordure discrète.
  // Fix 2026-09-07 (v2) : boutons like/commenter directement visibles sur
  // la carte, plus besoin de cliquer sur "Voir plus" pour réagir.
  const auteurEtudiant = item.auteur_role === 'etudiant';
  const auteurClasse = (item.auteur_classe || '').trim();
  const [lightbox, setLightbox] = useState<string | null>(null);

  const onImageClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (item.image_url) setLightbox(item.image_url);
  };

  return (
    <article className="bg-gray-900 border border-gray-800 hover:border-gray-700 rounded-xl overflow-hidden transition-colors">
      {/* Fix 2026-09-07 : ligne auteur (nom + classe si étudiant) au-dessus */}
      {item.auteur_nom && (
        <div className="px-4 pt-3 flex items-center gap-2 text-xs">
          <div className="w-7 h-7 rounded-full bg-gray-800 flex-shrink-0 flex items-center justify-center text-gray-400">
            {auteurEtudiant
              ? <GraduationCap size={12} />
              : item.auteur_role === 'formateur'
                ? <Briefcase size={12} />
                : <span className="text-[9px] font-bold text-gray-300">
                    {(item.auteur_nom.split(/\s+/).slice(0, 2).map(p => p[0] || '').join('') || 'S').toUpperCase()}
                  </span>}
          </div>
          <div className="min-w-0 flex-1 flex items-center gap-1.5 flex-wrap">
            <span className="font-semibold text-gray-200 truncate">{item.auteur_nom}</span>
            {auteurEtudiant && auteurClasse && (
              <span className="inline-flex items-center px-1.5 py-0.5 text-[9px] font-semibold rounded bg-blue-900/40 text-blue-300 border border-blue-800">
                {auteurClasse}
              </span>
            )}
          </div>
        </div>
      )}

      {/* En-tête compact : épingle + type + date */}
      <div className={`px-4 ${item.auteur_nom ? 'pt-1' : 'pt-3'} flex items-center gap-2 text-[11px] text-gray-500`}>
        {item.epinglee && (
          <span className="inline-flex items-center gap-1 text-gray-400">
            <Pin size={11} /> Épinglé
          </span>
        )}
        <span className="text-gray-600">
          {item.type_label || TYPE_LABELS[item.type] || item.type}
        </span>
        <span className="text-gray-700">·</span>
        <span>{fmtRelative(item.date_creation)}</span>
        {!item.a_lu && (
          <span className="ml-auto inline-block w-2 h-2 rounded-full bg-blue-500" title="Non lue" />
        )}
      </div>

      {/* Zone cliquable "ouvrir le détail" : titre + contenu */}
      <div
        onClick={onClick}
        className="px-4 pt-1.5 pb-3 cursor-pointer"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onClick()}
      >
        <h3 className="font-semibold text-gray-100 text-[15px] leading-snug">{item.titre}</h3>
        {item.contenu && (
          <div className="mt-1 text-sm text-gray-300 leading-relaxed">
            <ContenuExtensible texte={item.contenu} />
          </div>
        )}
      </div>

      {/* Image / vidéo pleine largeur (style Facebook) — image cliquable = lightbox */}
      {item.image_url && (
        <button
          type="button"
          onClick={onImageClick}
          className="block w-full bg-gray-800 overflow-hidden cursor-zoom-in"
          aria-label="Voir l'image en entier"
        >
          <img
            src={item.image_url}
            alt=""
            className="w-full h-auto max-h-[500px] object-cover"
            loading="lazy"
          />
        </button>
      )}
      {!item.image_url && item.video_url && (
        <div className="bg-black overflow-hidden" onClick={(e) => e.stopPropagation()}>
          <video src={item.video_url} controls className="w-full max-h-[500px] object-contain" preload="metadata" />
        </div>
      )}

      {/* Meta : date évènement / lieu (si présent) */}
      {(item.date_evenement || item.lieu) && (
        <div className="px-4 pt-3 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-400">
          {item.date_evenement && (
            <span className="inline-flex items-center gap-1">
              <Calendar size={11} /> {fmtDateHeure(item.date_evenement)}
            </span>
          )}
          {item.lieu && (
            <span className="inline-flex items-center gap-1">
              <MapPin size={11} /> {item.lieu}
            </span>
          )}
        </div>
      )}

      {/* Barre compteurs cliquables + "Voir plus →" */}
      <div className="px-4 py-2 mt-2 border-t border-gray-800 flex items-center justify-between text-[11px] text-gray-500">
        <div className="flex items-center gap-3">
          {item.nb_reactions_total > 0 && (
            <span className="inline-flex items-center gap-1">
              <ThumbsUp size={11} className="text-blue-400" fill="currentColor" />
              {item.nb_reactions_total} réaction{item.nb_reactions_total > 1 ? 's' : ''}
            </span>
          )}
          {(item.nb_commentaires || 0) > 0 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onOpenComments(); }}
              className="hover:text-gray-300"
            >
              {item.nb_commentaires} commentaire{(item.nb_commentaires || 0) > 1 ? 's' : ''}
            </button>
          )}
        </div>
        {/* Fix 2026-09-07 (v3) : bouton "Voir plus →" gardé visible pour
            ouvrir le détail (le user y tenait). Ne remplace pas les actions
            like/commenter ci-dessous. */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onClick(); }}
          className="text-blue-400 hover:text-blue-300 font-medium"
        >
          Voir plus →
        </button>
      </div>

      {/* Fix 2026-09-07 (v2) : boutons Réactions + Commenter TOUJOURS visibles
          sur chaque carte du fil, plus besoin d'ouvrir le détail. */}
      <div className="border-t border-gray-800 flex items-center gap-0.5 px-1">
        {REACTIONS.map((r) => {
          const active = item.ma_reaction === r.key;
          const count = item.reactions[r.key] || 0;
          return (
            <button
              key={r.key}
              type="button"
              onClick={(e) => { e.stopPropagation(); onReact(r.key); }}
              title={r.label}
              className={`flex-1 min-w-0 inline-flex items-center justify-center gap-1 py-2 rounded text-xs font-medium transition-colors ${
                active
                  ? 'text-blue-400 hover:bg-blue-900/20'
                  : 'text-gray-400 hover:bg-gray-800'
              }`}
            >
              {r.icon}
              {count > 0 && <span className="text-[10px]">{count}</span>}
            </button>
          );
        })}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onOpenComments(); }}
          className="flex-1 min-w-0 inline-flex items-center justify-center gap-1.5 py-2 rounded text-xs font-medium text-gray-400 hover:bg-gray-800 transition-colors"
          title="Commenter"
        >
          <MessageCircle size={14} />
          <span className="hidden xs:inline">Commenter</span>
        </button>
      </div>

      {lightbox && <ImageLightbox src={lightbox} onClose={() => setLightbox(null)} />}
    </article>
  );
};

// ─── Lightbox image plein écran ────────────────────────────────────
// Fix 2026-09-07 : permet à l'utilisateur de voir l'image en entier.
// Pincer/dézoomer natif via `touch-action: pinch-zoom`.
// Fix 2026-09-07 (v2) : NE PAS toucher à `document.body.style.overflow` —
// ça casse le pull-to-refresh natif si un cleanup rate. Le fond fixed+inset
// couvre l'écran, l'utilisateur ne voit pas le scroll derrière de toute façon.
// Fix 2026-09-07 (v3) : rendu via portal pour échapper à tout stacking
// context ancêtre (backdrop-filter du header, transform, etc.).
const ImageLightbox: React.FC<{ src: string; onClose: () => void }> = ({ src, onClose }) => {
  return createPortal(
    <div
      className="fixed inset-0 z-[80] bg-black flex items-center justify-center"
      onClick={onClose}
      style={{ touchAction: 'pinch-zoom' }}
    >
      <button
        onClick={onClose}
        aria-label="Fermer"
        className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/60 text-white hover:bg-black/80"
      >
        <X size={22} />
      </button>
      <img
        src={src}
        alt=""
        className="max-w-full max-h-full object-contain select-none"
        onClick={(e) => e.stopPropagation()}
        draggable={false}
      />
    </div>,
    document.body,
  );
};

// ─── Modal détail ─────────────────────────────────────────────────

const DetailModal: React.FC<{
  item: ActualiteItem;
  onClose: () => void;
  onReact: (type: string) => void;
}> = ({ item, onClose, onReact }) => {
  const [lightbox, setLightbox] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState(0);
  const touchStartY = useRef<number | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  // Fix 2026-09-07 (v2) : PAS de `document.body.style.overflow = hidden` —
  // ça cassait le pull-to-refresh natif si le cleanup rate. Le backdrop
  // fixed inset-0 couvre déjà l'écran, l'utilisateur ne voit pas le scroll
  // de la page derrière.

  // Ferme via touche Échap sur desktop.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !lightbox) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, lightbox]);

  // Fix 2026-09-07 (v2) : swipe vers le haut UNIQUEMENT sur la poignée en
  // haut du sheet — sinon on interférait avec le scroll interne des
  // commentaires + de la description. Ce pattern est celui d'iOS.
  const onHandleTouchStart = (e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
  };
  const onHandleTouchMove = (e: React.TouchEvent) => {
    if (touchStartY.current === null) return;
    const dy = e.touches[0].clientY - touchStartY.current;
    // Uniquement le mouvement vers le haut (dy négatif). Limité à -180px.
    if (dy < 0) setDragOffset(Math.max(dy, -180));
    else setDragOffset(0);
  };
  const onHandleTouchEnd = () => {
    if (dragOffset < -80) {
      onClose();
    }
    setDragOffset(0);
    touchStartY.current = null;
  };

  const auteurEtudiant = item.auteur_role === 'etudiant';
  const auteurFormateur = item.auteur_role === 'formateur';
  const auteurClasse = (item.auteur_classe || '').trim();
  const auteurNomAffiche = item.auteur_nom || 'Scolarité';

  // Fix 2026-09-07 (v3) : rendu via portal sur document.body pour échapper
  // au stacking context du header (backdrop-blur) et à tout parent qui
  // aurait un `transform` — sinon le modal peut se retrouver derrière ou
  // clipé, donnant un « écran noir » sur mobile.
  return createPortal(
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-gray-900 border border-gray-800 sm:rounded-2xl w-full max-w-2xl max-h-[92vh] rounded-t-2xl flex flex-col overflow-hidden transition-transform"
        style={dragOffset ? { transform: `translateY(${dragOffset}px)` } : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Indicateur "poignée" mobile — le SEUL endroit qui capte le swipe up */}
        <div
          className="pt-2 pb-1 flex flex-col items-center sm:hidden select-none cursor-grab active:cursor-grabbing"
          onTouchStart={onHandleTouchStart}
          onTouchMove={onHandleTouchMove}
          onTouchEnd={onHandleTouchEnd}
          style={{ touchAction: 'none' }}
        >
          <div className="w-10 h-1.5 rounded-full bg-gray-700 mb-1" />
          <div className="text-[10px] text-gray-500 flex items-center gap-1">
            <ChevronUp size={10} /> Balayez la poignée vers le haut pour fermer
          </div>
        </div>

        {/* Header : ← retour + auteur + type + fermer */}
        <div className="sticky top-0 bg-gray-900 border-b border-gray-800 px-3 py-2.5 flex items-center gap-2 z-10">
          <button
            onClick={onClose}
            aria-label="Retour aux publications"
            className="p-1.5 rounded-full text-gray-300 hover:text-white hover:bg-gray-800 flex-shrink-0"
            title="Retour"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="w-8 h-8 rounded-full bg-gray-800 flex-shrink-0 flex items-center justify-center text-gray-400">
            {auteurFormateur
              ? <Briefcase size={14} />
              : auteurEtudiant
                ? <GraduationCap size={14} />
                : <span className="text-[10px] font-bold text-gray-300">
                    {(auteurNomAffiche.split(/\s+/).slice(0, 2).map(p => p[0] || '').join('') || 'S').toUpperCase()}
                  </span>}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-gray-100 truncate flex items-center gap-1.5">
              <span className="truncate">{auteurNomAffiche}</span>
              {auteurEtudiant && auteurClasse && (
                <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold rounded bg-blue-900/40 text-blue-300 border border-blue-800 flex-shrink-0">
                  {auteurClasse}
                </span>
              )}
            </div>
            <div className="text-[11px] text-gray-500 flex items-center gap-1.5">
              <span className={`inline-flex items-center px-1.5 py-0.5 text-[9px] font-semibold rounded border ${
                TYPE_BADGE_CLS[item.type] || TYPE_BADGE_CLS.autre
              }`}>
                {item.type_label || TYPE_LABELS[item.type] || item.type}
              </span>
              <span>·</span>
              <span>{fmtRelative(item.date_creation)}</span>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Fermer"
            className="p-1.5 rounded-full text-gray-400 hover:text-gray-100 hover:bg-gray-800 flex-shrink-0"
          >
            <X size={18} />
          </button>
        </div>

        {/* Zone scrollable contenu */}
        <div
          ref={contentRef}
          className="overflow-y-auto flex-1"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {item.image_url && (
            <button
              type="button"
              onClick={() => setLightbox(item.image_url!)}
              className="block w-full bg-gray-800 cursor-zoom-in"
              aria-label="Voir l'image en entier"
            >
              <img
                src={item.image_url}
                alt=""
                className="w-full max-h-[70vh] object-contain bg-gray-950"
                loading="lazy"
              />
            </button>
          )}
          {!item.image_url && item.video_url && (
            <div className="bg-black">
              <video src={item.video_url} controls className="w-full max-h-[70vh] object-contain" />
            </div>
          )}

          <div className="p-4">
            <h2 className="text-lg font-bold text-gray-100 leading-tight">{item.titre}</h2>

            {(item.date_evenement || item.lieu) && (
              <div className="mt-3 flex flex-col gap-1 text-sm text-gray-300 bg-gray-950 border border-gray-800 rounded-lg p-3">
                {item.date_evenement && (
                  <div className="flex items-center gap-2">
                    <Calendar size={14} className="text-blue-400" />
                    <span>{fmtDateHeure(item.date_evenement)}</span>
                  </div>
                )}
                {item.lieu && (
                  <div className="flex items-center gap-2">
                    <MapPin size={14} className="text-blue-400" />
                    <span>{item.lieu}</span>
                  </div>
                )}
              </div>
            )}

            {item.contenu && (
              <div className="mt-4 text-sm text-gray-200 whitespace-pre-line leading-relaxed text-justify">
                {item.contenu}
              </div>
            )}

            {item.lien_externe && (
              <a
                href={item.lien_externe}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg"
              >
                <ExternalLink size={14} /> Voir le lien
              </a>
            )}

            <CommentairesActualite actualiteId={item.id} />

            {/* Espace pour éviter que le sticky footer masque le dernier commentaire */}
            <div className="h-24" />
          </div>
        </div>

        {/* Footer sticky : réactions + bouton commenter — toujours visible */}
        <div className="border-t border-gray-800 bg-gray-900/95 backdrop-blur px-2 py-2 flex items-center justify-between gap-1 flex-shrink-0">
          <div className="flex items-center gap-1 flex-wrap">
            {REACTIONS.map((r) => {
              const count = item.reactions[r.key] || 0;
              const active = item.ma_reaction === r.key;
              return (
                <button
                  key={r.key}
                  onClick={() => onReact(r.key)}
                  className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full border text-xs font-medium transition-colors ${
                    active
                      ? 'bg-blue-600 border-blue-500 text-white'
                      : 'bg-gray-950 border-gray-800 text-gray-300 hover:border-gray-700'
                  }`}
                  title={r.label}
                >
                  {r.icon}
                  {count > 0 && <span>{count}</span>}
                </button>
              );
            })}
          </div>
          <button
            onClick={() => {
              // Scroll vers la zone commentaires
              const el = contentRef.current?.querySelector('[data-commentaires]');
              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              else contentRef.current?.scrollTo({ top: contentRef.current.scrollHeight, behavior: 'smooth' });
            }}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-gray-800 bg-gray-950 text-gray-200 hover:border-gray-700 text-xs font-medium"
          >
            <MessageCircle size={14} />
            <span>{item.nb_commentaires ? `${item.nb_commentaires} commentaire${item.nb_commentaires > 1 ? 's' : ''}` : 'Commenter'}</span>
          </button>
        </div>
      </div>

      {lightbox && <ImageLightbox src={lightbox} onClose={() => setLightbox(null)} />}
    </div>,
    document.body,
  );
};
